import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDebounce, useScroll} from 'react-use';
import {
  useCart,
  CartLineProvider,
  Money,
  Image,
  ClientAnalytics,
} from '@shopify/hydrogen';

import {
  Button,
  CartLineItem,
  CartEmpty,
  Text,
  IconMinus,
  IconPlus,
} from '~/components';
// @ts-expect-error @headlessui/react
import {Dialog, Disclosure} from '@headlessui/react';
import iconClose from '../../assets/icons/icon-close.svg';
import {
  ANALYTIC_EVENT_NAMES,
  CUSTOM_ATTRIBUTES_KEYS,
  DATA_TEST_IDS,
} from '~/lib/const';
import {useProductCartItems} from '~/hooks/useProductCartItems';
import {getNumberID} from '~/lib';

export function CartDetails({
  layout,
  onClose,
}: {
  layout: 'drawer' | 'page';
  onClose?: () => void;
}) {
  const {lines, note, id, linesAdd, linesUpdate} = useCart();
  const scrollRef = useRef(null);
  const {y} = useScroll(scrollRef);

  const updateNote = async (note: string) => {
    await fetch('/api/update-note', {
      method: 'POST',
      body: JSON.stringify({
        cartId: id,
        note,
      }),
    });
  };

  const quantities = useMemo(() => {
    return lines.reduce((acc, line) => {
      const {quantity, merchandise, attributes} = line;

      const type = attributes.find(
        (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
      )?.value;

      if (merchandise && type !== 'pole') {
        const {id} = merchandise;

        if (acc[id]) {
          acc[id] += quantity;
        } else {
          acc[id] = quantity;
        }
      }

      return acc;
    }, {} as Record<string, number>);
  }, [lines]);

  const linesWithoutAddonsAndBundleProducts = useProductCartItems(lines);
  const addonLines = useMemo(() => {
    return lines.filter((line) => {
      const isAddon = line.attributes.find(
        (attr) =>
          attr.key === CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_VARIANT_ID,
      );
      return isAddon;
    });
  }, [lines]);

  const total = useMemo(
    () =>
      linesWithoutAddonsAndBundleProducts.reduce(
        (acc, line) => acc + line.quantity,
        0,
      ),
    [linesWithoutAddonsAndBundleProducts],
  );

  const container = {
    drawer: `grid grid-cols-1 grid-rows-[1fr_auto] flex-1`,
    page: 'pb-12 grid md:grid-cols-2 md:items-start gap-8 md:gap-8 lg:gap-12',
  };

  const content = {
    drawer: 'px-6 pb-6 sm-max:pt-2 overflow-auto transition md:px-[40px]',
    page: 'flex-grow md:translate-y-4',
  };

  const summary = {
    drawer: 'grid gap-5 px-6 pb-[15px] border-t md:px-[40px]',
    page: 'sticky top-nav grid gap-6 p-4 md:px-6 md:translate-y-4 bg-primary/5 rounded w-full',
  };

  let earnedGiftCards = 0;
  let giftCardLineId = '';
  let giftCardQty = 0;
  let auto = false;
  lines.forEach((line) => {
    line.attributes.forEach((attr) => {
      if (attr.key == '_giftcardeligible') {
        earnedGiftCards += line.quantity;
      }
      if (attr.key == '_auto') {
        auto = true;
      }
    });
    // @ts-ignore
    if (line.merchandise.sku == 'GFT25' && auto) {
      giftCardLineId = line.id;
      giftCardQty = line.quantity;
    }
  });

  if (giftCardLineId) {
    if (earnedGiftCards != giftCardQty) {
      linesUpdate([
        {
          id: giftCardLineId,
          quantity: earnedGiftCards,
          attributes: [
            {
              key: '_auto',
              value: 'true',
            },
          ],
        },
      ]);
    }
  } else if (earnedGiftCards > 0) {
    linesAdd([
      {
        merchandiseId: 'gid://shopify/ProductVariant/45152371441960',
        quantity: earnedGiftCards,
        attributes: [
          {
            key: '_auto',
            value: 'true',
          },
        ],
      },
    ]);
  }
  useEffect(() => {
    if (lines.length > 0) {
      ClientAnalytics.publish(ANALYTIC_EVENT_NAMES.VIEW_CART, true, {
        event: {
          _clear: true,
          event: ANALYTIC_EVENT_NAMES.VIEW_CART,
          ecommerce: {
            items: lines.map((line) => ({
              item_id: getNumberID(line.merchandise.id),
              item_name: line.merchandise.product.title,
              currency: 'USD',
              price: Number(line.cost.totalAmount.amount),
              quantity: line.quantity.toString(),
              item_variant: line.merchandise.title,
              item_brand:
                line.attributes.find(
                  (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.ITEM_VENDOR,
                )?.value ?? '',
              item_category:
                line.attributes.find(
                  (attr) =>
                    attr.key === CUSTOM_ATTRIBUTES_KEYS.ITEM_PRODUCT_TYPE,
                )?.value ?? '',
            })),
          },
        },
      });
    }

    // Ignore this effect because we only want to run it once
    // to avoid sending the event multiple times when the cart changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subTotal = useMemo(() => {
    let subTotal = 0;

    for (const line of lines) {
      const isBundleMain = line.attributes.find(
        (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_MAIN,
      );

      const isBundlePart = line.attributes.find(
        (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BUNDLE,
      );

      if (isBundleMain && isBundlePart) {
        subTotal += Number(line.cost.totalAmount.amount);
      } else if (!isBundleMain && isBundlePart) {
        continue;
      } else {
        subTotal += Number(line.cost.totalAmount.amount);
      }
    }

    return subTotal;
  }, [lines]);

  // const totalPrice = useMemo(() => {
  //   let subTotal = 0;

  //   for (const line of lines) {
  //     const isBundleMain = line.attributes.find(
  //       (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_MAIN,
  //     );

  //     const isBundlePart = line.attributes.find(
  //       (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BUNDLE,
  //     );

  //     if (isBundleMain && isBundlePart) {
  //       subTotal += Number(line.merchandise.priceV2.amount);
  //     } else if (!isBundleMain && isBundlePart) {
  //       continue;
  //     } else {
  //       subTotal += Number(line.merchandise.priceV2.amount);
  //     }
  //   }

  //   return subTotal;
  // }, [lines]);

  return (
    <>
      <header
        data-testid={DATA_TEST_IDS.CART_DETAILS}
        className={`sticky top-0 items-center pl-10 pr-5 pt-[16px] z-16 bg-gray-200 justify-between`}
      >
        <div className={`text-center`}>
          {/* <div>
            <b className="text-lg">COMPLETE PACKAGE SALE!</b>
          </div>
          <div className="text-s mb-3 mt-2">
            Save an Extra 10% on All Complete Gear Packages
          </div> */}
          {/* <div>
            {totalPrice < 1000 && (
              <i className={`text-xs`}>
                Add ${(1000 - totalPrice).toFixed(2)} to your cart to enjoy 15%
                off your order
              </i>
            )}
            {totalPrice >= 1000 && (
              <i className={`text-xs`}>You are saving 15%</i>
            )}
          </div> */}
        </div>
        <div
          className={`flex items-center pl-10 pr-5 pt-[10px] z-20 bg-gray-200 justify-between`}
        >
          <Dialog.Title className="flex items-center cursor-pointer">
            <span
              data-testid={DATA_TEST_IDS.CART_TOTAL(total)}
              className="text-28/28 font-header font-extrabold"
            >
              CART ({total})
            </span>
          </Dialog.Title>
          <button
            data-testid={DATA_TEST_IDS.BUTTON('close')}
            type="button"
            className="w-12 h-12 rounded-full flex justify-center items-center bg-white"
            onClick={onClose}
          >
            <Image
              loading="lazy"
              src={iconClose}
              width={17}
              height={17}
              alt="Close modal"
            />
          </button>
        </div>
      </header>

      {lines.length === 0 ? (
        <CartEmpty onClose={onClose} layout={layout} />
      ) : (
        <div className="grid mt-[40px]">
          <form
            className={container[layout]}
            style={{
              height: `calc(var(--screen-height, 100vh) - var(--height-nav))`,
            }}
          >
            <section
              ref={scrollRef}
              aria-labelledby="cart-contents"
              className={`${content[layout]} ${y > 0 ? 'border-t' : ''}`}
            >
              <ul className="grid gap-6 md:gap-10">
                {linesWithoutAddonsAndBundleProducts.map((line) => (
                  <CartLineProvider key={line.id} line={line}>
                    <CartLineItem
                      quantities={quantities}
                      addonLines={addonLines}
                    />
                  </CartLineProvider>
                ))}
                {/* {addonLines?.map((line) => (
                  <CartLineProvider key={line.id} line={line}>
                    <CartLineItem quantities={quantities} />
                  </CartLineProvider>
                ))} */}
              </ul>
            </section>
            <section
              aria-labelledby="summary-heading"
              className={summary[layout]}
            >
              <h2
                data-testid={DATA_TEST_IDS.ORDER_SUMMARY}
                id="summary-heading"
                className="sr-only"
              >
                Order summary
              </h2>
              <CartCheckoutInstructions
                note={note ?? ''}
                onChange={updateNote}
              />
              <OrderSummary subTotal={subTotal} />
              <CartCheckoutActions subTotal={subTotal} />
            </section>
          </form>
        </div>
      )}
    </>
  );
}

function CartCheckoutInstructions(props: {
  note: string;
  onChange: (value: string) => void;
}) {
  const [value, setValue] = useState(props.note);

  useDebounce(() => props.onChange(value), 500, [value]);

  return (
    <div
      data-testid={DATA_TEST_IDS.CHECKOUT_INSTRUCTIONS}
      className="border-y border-y-gray-300 py-5"
    >
      <Disclosure as="div" className="grid w-full">
        {({open}) => (
          <>
            <Disclosure.Button className="text-left">
              <div className="flex justify-between">
                <div className="text-16/20 font-header font-bold">
                  ADD SPECIAL INSTRUCTIONS TO YOUR ORDER
                </div>
                {open ? (
                  <IconMinus
                    data-testid={DATA_TEST_IDS.ICON('minus')}
                    className="mr-3"
                  />
                ) : (
                  <IconPlus
                    data-testid={DATA_TEST_IDS.ICON('plus')}
                    className="mr-3"
                  />
                )}
              </div>
            </Disclosure.Button>

            <Disclosure.Panel>
              <textarea
                data-testid={DATA_TEST_IDS.TEXT_AREA('checkout-instructions')}
                className="w-full h-[102px] border border-gray-100 mt-2.5 mb-[5px]"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
}

function CartCheckoutActions({subTotal}: {subTotal: number}) {
  const {id, checkoutUrl, lines, discountCodesUpdate} = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = useCallback(async () => {
    setLoading(true);

    discountCodesUpdate([]);

    // @ts-ignore
    gtag({
      _clear: true,
      event: ANALYTIC_EVENT_NAMES.BEGIN_CHECKOUT,
      ecommerce: {
        value: subTotal,
        currency: 'USD',
        items: lines.map((line) => ({
          item_id: getNumberID(line.merchandise.id),
          item_name: line.merchandise.product.title,
          currency: 'USD',
          price: Number(line.cost.totalAmount.amount),
          quantity: line.quantity.toString(),
          item_variant: line.merchandise.title,
          item_brand:
            line.attributes.find(
              (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.ITEM_VENDOR,
            )?.value ?? '',
          item_category:
            line.attributes.find(
              (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.ITEM_PRODUCT_TYPE,
            )?.value ?? '',
        })),
      },
    });

    try {
      const request = await fetch('/api/create-discount', {
        method: 'POST',
        body: JSON.stringify({
          cartId: id,
        }),
      });

      if (request.status !== 200) {
        console.error('Error creating discount');
      }
    } catch (error) {
      console.error(error);
    }

    try {
      const request = await fetch('/api/create-checkout', {
        method: 'POST',
        body: JSON.stringify({
          checkoutUrl,
        }),
      });

      const response = await request.json();

      if (request.status === 200 && response.url) {
        console.error('Error creating checkout');

        setTimeout(() => {
          setLoading(false);
          window.location.href = response.url;
        }, 100);
      } else {
        setTimeout(() => {
          setLoading(false);
          window.location.href = checkoutUrl!;
        }, 100);
      }
    } catch (error) {
      console.error(error);

      setTimeout(() => {
        setLoading(false);
        window.location.href = checkoutUrl!;
      }, 100);
    }
  }, [discountCodesUpdate, subTotal, lines, id, checkoutUrl]);

  return (
    <>
      <div className="grid gap-[18px]">
        {checkoutUrl && (
          <Button
            data-testid={DATA_TEST_IDS.BUTTON('checkout')}
            loading={loading}
            as="span"
            width="full"
            variant="blue"
            className="py-[27px] cursor-pointer"
            onClick={handleCheckout}
          >
            <span className="text-20/26 text-white">CHECKOUT</span>
          </Button>
        )}
      </div>
    </>
  );
}

function OrderSummary({subTotal}: {subTotal: number}) {
  const {cost} = useCart();

  return (
    <>
      <dl className="grid">
        <div className="flex items-center justify-between">
          <Text as="h4">SUBTOTAL</Text>
          <Text as="h4">
            <Money
              data={{
                amount: subTotal.toString(),
                currencyCode: cost?.totalAmount.currencyCode,
              }}
            />
          </Text>
        </div>
      </dl>
    </>
  );
}
